import React, { useEffect, useId, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

interface CategoryMermaidDiagramProps {
  chart: string;
  testId?: string;
  titleKey?: string;
  titleFallback?: string;
}

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.15;
const VIEWPORT_PADDING = 20;

const clampZoom = (value: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const CategoryMermaidDiagram: React.FC<CategoryMermaidDiagramProps> = ({
  chart,
  testId = "category-mermaid-diagram",
  titleKey,
  titleFallback
}) => {
  const { t } = useKivunovaTranslation();
  const svgHostRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const reactId = useId().replace(/:/g, "");
  const [renderError, setRenderError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const fitToViewport = () => {
    const viewport = viewportRef.current;
    const host = svgHostRef.current;
    const svgEl = host?.querySelector("svg");
    if (!viewport || !svgEl) {
      return;
    }

    const hostStyle = window.getComputedStyle(host);
    const hostPadX =
      parseFloat(hostStyle.paddingLeft) + parseFloat(hostStyle.paddingRight);
    const hostPadY =
      parseFloat(hostStyle.paddingTop) + parseFloat(hostStyle.paddingBottom);

    const availableWidth = viewport.clientWidth - VIEWPORT_PADDING - hostPadX;
    const availableHeight = viewport.clientHeight - VIEWPORT_PADDING - hostPadY;

    const bbox = svgEl.getBBox();
    const svgWidth = bbox.width;
    const svgHeight = bbox.height;

    if (svgWidth <= 0 || svgHeight <= 0) {
      return;
    }

    const scaleX = availableWidth / svgWidth;
    const scaleY = availableHeight / svgHeight;
    const fitZoom = clampZoom(Math.min(scaleX, scaleY) * 0.96);

    setZoom(fitZoom);
    setPan({
      x: Math.max(0, (viewport.clientWidth - svgWidth * fitZoom) / 2),
      y: Math.max(8, (viewport.clientHeight - svgHeight * fitZoom) / 2)
    });
  };

  const resetView = () => {
    fitToViewport();
  };

  const zoomIn = () => {
    setZoom((current) => clampZoom(current + ZOOM_STEP));
  };

  const zoomOut = () => {
    setZoom((current) => clampZoom(current - ZOOM_STEP));
  };

  useEffect(() => {
    let cancelled = false;
    const container = svgHostRef.current;
    if (!container) {
      return undefined;
    }

    setRenderError(null);
    container.innerHTML = "";

    const renderChart = async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "loose",
          theme: "base",
          themeVariables: {
            primaryColor: alpha(BRAND_COLOR, 0.1),
            primaryTextColor: "#1a1a1a",
            primaryBorderColor: BRAND_COLOR,
            lineColor: alpha(BRAND_COLOR, 0.45),
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: "11px"
          },
          flowchart: {
            htmlLabels: false,
            curve: "basis",
            padding: 6,
            nodeSpacing: 14,
            rankSpacing: 22,
            useMaxWidth: false
          }
        });

        const renderId = `category-mermaid-${reactId}-${Date.now()}`;
        const { svg } = await mermaid.render(renderId, chart.trim());

        if (cancelled || !svgHostRef.current) {
          return;
        }

        svgHostRef.current.innerHTML = svg;
        const svgEl = svgHostRef.current.querySelector("svg");
        if (svgEl) {
          svgEl.style.display = "block";
          svgEl.style.maxWidth = "none";
        }

        requestAnimationFrame(() => {
          if (!cancelled) {
            fitToViewport();
          }
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to render category mermaid diagram:", error);
          setRenderError(
            error instanceof Error ? error.message : "Diagram render failed"
          );
        }
      }
    };

    renderChart();

    return () => {
      cancelled = true;
    };
  }, [chart, reactId]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom((current) => clampZoom(current + delta));
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      viewport.removeEventListener("wheel", onWheel);
    };
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsPanning(true);
    panStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) {
      return;
    }
    setPan({
      x: panStartRef.current.panX + event.clientX - panStartRef.current.x,
      y: panStartRef.current.panY + event.clientY - panStartRef.current.y
    });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) {
      return;
    }
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsPanning(false);
  };

  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(BRAND_COLOR, 0.14)}`,
        bgcolor: alpha(BRAND_COLOR, 0.04),
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${alpha(BRAND_COLOR, 0.1)}`,
          bgcolor: alpha(BRAND_COLOR, 0.06)
        }}
      >
        {titleKey ? (
          <Typography variant="subtitle2" fontWeight={700}>
            {t(titleKey, titleFallback ?? "")}
          </Typography>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 0.5, display: { xs: "none", sm: "block" } }}
          >
            {t(
              "categories-learn-more-diagram-hint",
              "Scroll to zoom · Drag to pan"
            )}
          </Typography>
          <Tooltip
            title={t("categories-learn-more-diagram-zoom-out", "Zoom out")}
          >
            <span>
              <IconButton
                size="small"
                onClick={zoomOut}
                aria-label={t(
                  "categories-learn-more-diagram-zoom-out",
                  "Zoom out"
                )}
                data-testid={`${testId}-zoom-out`}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{ minWidth: 40, textAlign: "center" }}
            data-testid={`${testId}-zoom-level`}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip
            title={t("categories-learn-more-diagram-zoom-in", "Zoom in")}
          >
            <span>
              <IconButton
                size="small"
                onClick={zoomIn}
                aria-label={t(
                  "categories-learn-more-diagram-zoom-in",
                  "Zoom in"
                )}
                data-testid={`${testId}-zoom-in`}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t("categories-learn-more-diagram-reset", "Fit to view")}
          >
            <span>
              <IconButton
                size="small"
                onClick={resetView}
                aria-label={t(
                  "categories-learn-more-diagram-reset",
                  "Fit to view"
                )}
                data-testid={`${testId}-zoom-reset`}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        ref={viewportRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        data-testid={`${testId}-viewport`}
        sx={{
          position: "relative",
          height: { xs: 320, sm: 400 },
          overflow: "hidden",
          cursor: isPanning ? "grabbing" : "grab",
          touchAction: "none",
          bgcolor: alpha(BRAND_COLOR, 0.02)
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            transition: isPanning ? "none" : "transform 0.12s ease-out",
            px: 1,
            py: 1,
            minWidth: 1,
            minHeight: 1
          }}
        >
          <Box
            ref={svgHostRef}
            sx={{
              "& svg": {
                display: "block",
                maxWidth: "none",
                width: "auto",
                height: "auto"
              },
              "& .node rect, & .node polygon, & .node circle": {
                strokeWidth: 1.25
              },
              "& .nodeLabel, & .label": {
                fontSize: "11px !important"
              }
            }}
          />
        </Box>
      </Box>

      {renderError ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ px: 2, pb: 1.5, display: "block" }}
        >
          {renderError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CategoryMermaidDiagram;
